import React from "react";
import { Toolbar } from "@mui/material";
import { Button } from "../../Button/Button";
import { HeartIcon } from "../../Icons/Miscellaneous/HeartIcon";
import { BellIcon } from "../../Icons/AlertsNotifications/BellIcon";
import logoIcon from "../../../Recursos/genesis_logo_icon.webp";
import { useDispatch, useSelector } from "react-redux";
import { setActiveDrawer } from "../../../Slices/uiSlice";
import {
  markNotificacionesAsRead,
  resetUnreadCount,
} from "../../../Slices/NotificacionesUsuario/notificacionesUsuarioSlice";
import { DRAWER_TYPES } from "Constantes/drawers";
import { useNavigate } from "react-router-dom";
import { RUTAS } from "rutas";
import { paramService } from "Services/param_service";
import { useQuery } from "Hooks/useQuery";
import { storageService } from "Services/storage_service";
import Badge from "@mui/material/Badge";
import "./NavbarMobile.scss";

/**
 * NavbarMobile component que renderiza la barra de navegación para dispositivos móviles.
 * Cambia su layout y funcionalidad según el estado de autenticación del usuario.
 *
 * @param {Object} props - Las props del componente
 * @param {boolean} props.isAuthenticated - Indica si el usuario está autenticado
 * @returns {JSX.Element} Toolbar mobile con logo, botones de acción y navegación
 */
export const NavbarMobile = ({ isAuthenticated }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activeDrawer = useSelector((state) => state.ui.activeDrawer);
  const { notificacionesUsuario, unreadCount } = useSelector(
    (state) => state.notificacionesUsuario
  );
  const cookieUser = storageService.getItem("user");

  const { data: images } = useQuery(paramService.param, {
    grupo: "imagenes",
    files: true,
  });

  const logoImg =
    images?.data.find((item) => item.codigoParametro === "logo_mobile")?.url ||
    "#";

  const handleLoginClick = () => {
    dispatch(setActiveDrawer(DRAWER_TYPES.LOGIN));
  };

  const handleMenuClick = () => {
    dispatch(setActiveDrawer(DRAWER_TYPES.MENU));
  };

  const handleFavoritesClick = () => {
    navigate(RUTAS.MIS_FAVORITOS);
    dispatch(setActiveDrawer(null));
  };

  const handleNotificationsClick = async () => {
    const isOpening = activeDrawer !== DRAWER_TYPES.NOTIFICATIONS;

    if (isOpening && cookieUser?.idUsuario) {
      dispatch(resetUnreadCount());
      
      if (notificacionesUsuario.length > 0) {
        const unreadNotifications = notificacionesUsuario
          .filter((notification) => !notification.fechaLectura)
          .map((notification) => ({
            idNotificacionUsuario: notification.idNotificacionUsuario,
            fechaLectura: new Date().toISOString(),
          }));

        if (unreadNotifications.length > 0) {
          await dispatch(markNotificacionesAsRead(unreadNotifications));
        }
      }
    }

    dispatch(setActiveDrawer(activeDrawer ? null : DRAWER_TYPES.NOTIFICATIONS));
  };

  const handleLogoClick = () => {
    navigate(RUTAS.PRINCIPAL);
    dispatch(setActiveDrawer(null));
  };


  if (!isAuthenticated) {
    return (
      <Toolbar className="navbar-mobile">
        <div className="navbar-mobile__logo" onClick={handleLogoClick}>
          <img
            src={logoImg}
            alt="Genesis Logo"
            onError={(e) => {
              e.target.src = logoIcon;
            }}
          />
        </div>
        <div className="navbar-mobile__actions">
          <Button
            variant="secundario-cta"
            size="mediano"
            onClick={handleLoginClick}
            className="paragraph-m-bold text-cian"
          >
            INICIAR SESIÓN
          </Button>
        </div>
      </Toolbar>
    );
  }

  return (
    <Toolbar className="navbar-mobile">
      <div className="navbar-mobile__menu">
        <Button
          variant="secundario-cta"
          size="mediano"
          onClick={handleMenuClick}
          className="paragraph-m-bold text-cian"
        >
          MENÚ
        </Button>
      </div>
      <div className="navbar-mobile__logo" onClick={handleLogoClick}>
        <img
          src={logoImg}
          alt="Genesis Logo"
          onError={(e) => {
            e.target.src = logoIcon;
          }}
        />
      </div>
      <div className="navbar-mobile__actions">
        <Button variant="icon" size="medium" onClick={handleFavoritesClick}>
          <HeartIcon />
        </Button>
        <Button variant="icon" size="medium" onClick={handleNotificationsClick}>
          <Badge
            badgeContent={unreadCount}
            className="navbar-mobile__notification-badge"
          >
            <BellIcon />
          </Badge>
        </Button>
      </div>
    </Toolbar>
  );
};
